import { useMemo } from "react";
import styled from "styled-components";
import type {
  ComboBoxOption,
  ComboBoxOptions,
  InterstateOnChangeEvent,
  TextInputEventValue
} from "@dealer-center/core-components";
import { Button, ComboBox, TextInput } from "@dealer-center/core-components";
import type {
  Trim,
  TrimCreatorFormProps,
  TrimMap
} from "@dealer-center/inventory-pages-gvm-interface";
import { LocaleYearMakeFilters, TrimMapTable } from "../Common";
import {
  comboBoxValue,
  valueFromComboBox,
  valuesToComboBoxOptions
} from "../util/comboBoxHelper";
import { useIsGvmRwAccessUser } from "../util/gvmUserUtil";

const StyledSearchForm = styled.div`
  box-shadow: 3px 3px 2px #999;
  border: 1px #ddd solid;
  margin-right: 10px;
  padding: 8px;
  min-width: 745px;
  min-height: 100px;
  max-width: 100%;
  display: grid;
  gap: 10px;
  grid-template-columns: 2fr 4fr;
`;

const resetInputs = {
  model: undefined,
  bodystyle: undefined,
  trimId: undefined,
  uniqueTrim: undefined,
  mappings: undefined
};

function trimsToComboBoxOptions(trims: Trim[] | undefined): ComboBoxOption[] {
  if (!trims) {
    return [];
  }
  return trims.map(item => ({
    value: `${item.globalVehicleTrimId}`,
    label: `${item.uniqueTrim} ${item.engineCylinderConfig || ""}${
      item.engineCylinders
    } ${item.driveTrain}  [${item.globalVehicleTrimId}]`
  }));
}

function trimToCloneComboBoxValue(
  trims: Trim[] | undefined,
  trimId: string | undefined
): ComboBoxOption[] {
  if (!trims || !trimId) {
    return [];
  }
  const selectedTrim = trims.find(trim => trim.globalVehicleTrimId === trimId);
  return selectedTrim ? trimsToComboBoxOptions([selectedTrim]) : [];
}

export const TrimCreatorForm = ({
  localeYearMakeSearchResponse,
  modelSearchResponse,
  setTrimCreateRequest,
  trimCreateRequest,
  trimToCloneSearchResponse,
  trimSearchResponse,
  createTrimMutation
}: TrimCreatorFormProps) => {
  const isReadyToSave =
    !!trimCreateRequest?.stage &&
    !!trimCreateRequest?.locale &&
    !!trimCreateRequest?.year &&
    !!trimCreateRequest?.make &&
    !!trimCreateRequest?.model &&
    !!trimCreateRequest?.bodystyle &&
    !!trimCreateRequest?.trimId &&
    !!trimCreateRequest?.uniqueTrim &&
    trimCreateRequest.uniqueTrim.trim().length > 0;

  const isGvmRwAccessUser = useIsGvmRwAccessUser();
  const isElementDisabled =
    !isGvmRwAccessUser || !isReadyToSave || createTrimMutation.isPending;

  const modelOptions: ComboBoxOption[] = useMemo(
    () => valuesToComboBoxOptions(modelSearchResponse?.data?.models),
    [modelSearchResponse?.data?.models]
  );
  const bodyStyleOptions: ComboBoxOption[] = useMemo(
    () => valuesToComboBoxOptions(modelSearchResponse?.data?.bodystyles),
    [modelSearchResponse?.data?.bodystyles]
  );
  const trimToCloneOptions: ComboBoxOption[] = useMemo(
    () => trimsToComboBoxOptions(trimToCloneSearchResponse?.data),
    [trimToCloneSearchResponse?.data]
  );
  const selectedModel: ComboBoxOption[] = useMemo(
    () => comboBoxValue(trimCreateRequest.model),
    [trimCreateRequest.model]
  );
  const selectedBodyStyle: ComboBoxOption[] = useMemo(
    () => comboBoxValue(trimCreateRequest.bodystyle),
    [trimCreateRequest.bodystyle]
  );
  const selectedTrim: ComboBoxOption[] = useMemo(
    () =>
      trimToCloneComboBoxValue(
        trimToCloneSearchResponse?.data,
        trimCreateRequest.trimId
      ),
    [trimToCloneSearchResponse?.data, trimCreateRequest.trimId]
  );

  const handleUniqueTrimChange = (
    event: InterstateOnChangeEvent<TextInputEventValue>
  ) => {
    const name = event.target.name || "";
    const value = event.target.value;

    setTrimCreateRequest({ ...trimCreateRequest, [name]: value });
  };

  const handleComboChange = (
    event: InterstateOnChangeEvent<ComboBoxOptions>
  ) => {
    const name = event.target.name || "";
    const value = event.target.value;
    let dependentAttr = {};

    if (name === "model") {
      dependentAttr = {
        bodystyle: undefined,
        trimId: undefined,
        uniqueTrim: undefined,
        mappings: undefined
      };
    } else if (name === "bodystyle") {
      dependentAttr = {
        trimId: undefined,
        uniqueTrim: undefined,
        mappings: undefined
      };
    }

    setTrimCreateRequest({
      ...trimCreateRequest,
      [name]: valueFromComboBox(value),
      ...dependentAttr
    });
  };

  const handleTrimToCloneChange = (
    event: InterstateOnChangeEvent<ComboBoxOptions>
  ) => {
    const name = event.target.name || "";
    const trimId = valueFromComboBox(event.target.value);
    const trim = (trimToCloneSearchResponse?.data ?? []).find(
      trim => trim.globalVehicleTrimId === trimId
    );

    setTrimCreateRequest({
      ...trimCreateRequest,
      [name]: trim?.globalVehicleTrimId,
      uniqueTrim: trim?.uniqueTrim,
      mappings: undefined
    });
  };

  const handleTrimCreate = () => {
    createTrimMutation.mutate(trimCreateRequest, {
      onSuccess: () => {
        setTrimCreateRequest({ stage: "LIVE", locale: "US_EN" });
      }
    });
  };

  const setTrimMapList = (trimMapList: TrimMap[]) => {
    setTrimCreateRequest({ ...trimCreateRequest, mappings: trimMapList });
  };

  const handleDelete = (record: TrimMap) => {
    const mappings = (trimCreateRequest.mappings || []).filter(
      item => item !== record
    );
    setTrimMapList(mappings);
  };

  const handleAdd = (record: TrimMap) => {
    const mappings = [...(trimCreateRequest.mappings || []), record];
    setTrimMapList(mappings);
  };

  const render = () => {
    return (
      <StyledSearchForm>
        <div>
          <h2>Trim Creation</h2>
          <LocaleYearMakeFilters
            localeYearMakeRequest={trimCreateRequest}
            queryData={localeYearMakeSearchResponse}
            resettableProps={resetInputs}
            setLocaleYearMakeRequest={setTrimCreateRequest}
          />
          <ComboBox
            name="model"
            options={modelOptions}
            placeholder="Select Model"
            value={selectedModel}
            onChange={handleComboChange}
          />
          <ComboBox
            name="bodystyle"
            options={bodyStyleOptions}
            placeholder="Select Body Style"
            value={selectedBodyStyle}
            onChange={handleComboChange}
          />
          <ComboBox
            isLoading={trimToCloneSearchResponse.isLoading}
            name="trimId"
            options={trimToCloneOptions}
            placeholder="Select Trim to Clone"
            value={selectedTrim}
            onChange={handleTrimToCloneChange}
          />
          <TextInput
            maxLength={150}
            name="uniqueTrim"
            placeholder="Unique Trim"
            value={trimCreateRequest?.uniqueTrim || ""}
            onChange={handleUniqueTrimChange}
          />
          <Button
            analyticsConfiguration={{
              eventName: "Create Trim button clicked"
            }}
            buttonStyle="primary"
            disabled={isElementDisabled}
            sx={{ width: "100%", marginTop: "10px" }}
            type="button"
            onClick={handleTrimCreate}
          >
            {createTrimMutation.isPending ? "Saving..." : "Create Trim"}
          </Button>
        </div>
        {trimCreateRequest.trimId && (
          <div
            style={{
              marginTop: "20px",
              border: "1px solid #ccc",
              padding: "10px",
              backgroundColor: "#f9f9f9"
            }}
          >
            <TrimMapTable
              handleAdd={handleAdd}
              handleDelete={handleDelete}
              isLoading={trimSearchResponse.isLoading}
              trimMapList={trimCreateRequest.mappings || []}
            />
          </div>
        )}
      </StyledSearchForm>
    );
  };
  return render();
};
